import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Heart, MessageCircle, Send, Bookmark, ChevronLeft, ChevronRight } from "lucide-react";
import type { SocialPost } from "@/types";
import { cn } from "@/lib/utils";

interface DetailModalProps {
  post: SocialPost | null;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

import { readMetric, METRIC_KEYS } from "@/lib/api";

/* ---- Read raw metrics exactly as the platform sent them ---- */
function getMetric(raw: unknown, keys: readonly string[]): number | null {
  return readMetric(raw, keys);
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString();
}

export function DetailModal({ post, onClose, onNext, onPrev }: DetailModalProps) {
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  useEffect(() => {
    if (post) {
      document.body.style.overflow = "hidden";
      setCurrentMediaIndex(0);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [post]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && onNext) onNext();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
    },
    [onClose, onNext, onPrev],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (!post) return null;

  const account = post.socialAccounts[0];
  const mediaCount = post.media.length;
  const currentMedia = post.media[currentMediaIndex];
  const isVideo = currentMedia?.type === "video";

  // Read platform-specific fields without lying about cross-platform meaning
  const likes = getMetric(post.rawMetrics, METRIC_KEYS.likes);
  const comments = getMetric(post.rawMetrics, METRIC_KEYS.comments);
  const shares = getMetric(post.rawMetrics, METRIC_KEYS.shares);
  const views = getMetric(post.rawMetrics, METRIC_KEYS.views);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 text-white/80 hover:text-white transition-colors"
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Navigation arrows */}
      {onPrev && (
        <button
          onClick={onPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 text-white/60 hover:text-white transition-colors"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}
      {onNext && (
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 text-white/60 hover:text-white transition-colors"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Main content */}
      <div className="flex flex-col sm:flex-row w-full max-w-6xl h-full sm:h-[90vh] bg-background sm:rounded-lg overflow-hidden">
        {/* Media area */}
        <div className="relative flex-1 bg-black flex items-center justify-center min-h-[50vh] sm:min-h-0">
          {currentMedia?.url ? (
            isVideo ? (
              <video
                src={currentMedia.url}
                className="max-w-full max-h-full object-contain"
                controls
                autoPlay
                muted={false}
                playsInline
                preload="metadata"
              />
            ) : (
              <img
                src={currentMedia.url}
                alt={post.caption}
                className="max-w-full max-h-full object-contain"
              />
            )
          ) : (
            <div className="text-white/40 text-sm">No media</div>
          )}

          {/* In-modal carousel arrows */}
          {mediaCount > 1 && (
            <>
              <button
                onClick={() => setCurrentMediaIndex((i) => (i > 0 ? i - 1 : mediaCount - 1))}
                className="absolute left-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 text-white/80 hover:bg-black/60 transition-colors"
                aria-label="Previous media"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setCurrentMediaIndex((i) => (i < mediaCount - 1 ? i + 1 : 0))}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 text-white/80 hover:bg-black/60 transition-colors"
                aria-label="Next media"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Media pagination dots */}
          {mediaCount > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
              {post.media.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentMediaIndex(i)}
                  className={cn(
                    "w-1.5 h-1.5 rounded-full transition-all",
                    i === currentMediaIndex ? "bg-white w-4" : "bg-white/40",
                  )}
                />
              ))}
            </div>
          )}
        </div>

        {/* Info panel */}
        <div className="w-full sm:w-[380px] flex flex-col bg-background border-l border-border">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-border">
            <div className="w-8 h-8 rounded-full bg-muted overflow-hidden">
              {account?.profilePhotoUrl ? (
                <img
                  src={account.profilePhotoUrl}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs font-semibold text-muted-foreground">
                  {(account?.username || post.platform).charAt(0)}
                </div>
              )}
            </div>
            <span className="text-sm font-semibold">{account?.username || post.platform}</span>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <p className="text-sm leading-relaxed">
              <span className="font-semibold mr-1">{account?.username || post.platform}</span>
              {post.caption}
            </p>
            <p className="text-xs text-muted-foreground">
              {new Date(post.createdAt).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>

          {/* Action bar */}
          <div className="p-4 border-t border-border space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setLiked(!liked)}
                  className="transition-transform active:scale-90"
                >
                  <Heart
                    className={cn(
                      "w-6 h-6 transition-all",
                      liked
                        ? "fill-red-500 stroke-red-500"
                        : "stroke-foreground hover:stroke-red-500",
                    )}
                    strokeWidth={liked ? 0 : 1.5}
                  />
                </button>
                <button className="stroke-foreground hover:stroke-primary transition-colors">
                  <MessageCircle className="w-6 h-6" strokeWidth={1.5} />
                </button>
                <button className="stroke-foreground hover:stroke-primary transition-colors">
                  <Send
                    className="w-[22px] h-[22px] -rotate-12 translate-y-0.5"
                    strokeWidth={1.5}
                  />
                </button>
              </div>
              <button
                onClick={() => setSaved(!saved)}
                className="transition-transform active:scale-90"
              >
                <Bookmark
                  className={cn(
                    "w-6 h-6 transition-all",
                    saved ? "fill-primary stroke-foreground" : "stroke-foreground",
                  )}
                  strokeWidth={saved ? 0 : 1.5}
                />
              </button>
            </div>

            {/* Metrics — render only what the platform reported, with honest labels */}
            <div className="space-y-1">
              {likes !== null && (
                <p className="text-sm font-semibold">
                  {formatCount(likes + (liked ? 1 : 0))} likes
                </p>
              )}
              {views !== null && (
                <p className="text-sm font-semibold">{formatCount(views)} views</p>
              )}
              {comments !== null && comments > 0 && (
                <p className="text-sm text-muted-foreground">
                  View all {formatCount(comments)} comments
                </p>
              )}
              {shares !== null && shares > 0 && (
                <p className="text-sm text-muted-foreground">{formatCount(shares)} shares</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
